import React, { useMemo, useState } from 'react';
import { Icon } from '@blueprintjs/core';
import { ace_index } from '@axo/shared/data-access/types';
import { ace_index_hooks } from '@axo/shared/data-access/hooks';
import { WorkingListCard } from './WorkingListCard';
import { CardRow } from './CardRow';
import { reorderPriorities } from './orderUtils';
import { FilterSet } from '../FilterSet';

export type Reorder = ReturnType<typeof useReorder>;

export function useReorder(lists: ace_index.WorkingList[]) {
  const update = ace_index_hooks.useUpdateWorkingList();

  interface Insert {
    workingList: ace_index.WorkingList;
    index: number;
  }

  const [drag, setDrag] = useState<
    Insert & {
      rect: DOMRect;
    }
  >();
  const [queue, setQueue] = useState<Insert[]>([]);

  const order = useMemo(() => {
    const items = drag ? [...queue, drag] : queue;

    return items.reduce(
      (acc, insert) => {
        const id = insert.workingList.ID;
        const before = acc.slice(0, insert.index).filter((x) => x !== id);
        const after = acc.slice(insert.index).filter((x) => x !== id);
        return [...before, id, ...after];
      },
      lists.map((x) => x.ID),
    );
  }, [lists, drag, queue]);

  return {
    ordered: order
      .map((id) => lists.find((x) => x.ID === id))
      .filter((x): x is ace_index.WorkingList => !!x),
    isLoading: update.isLoading,
    dragging: drag?.workingList,
    discard() {
      setDrag(undefined);
      setQueue([]);
    },
    hasChanges: queue.length > 0,
    async save() {
      const priorities = lists.reduce(
        (acc, x) => ({ ...acc, [x.ID]: x.Priority }),
        {} as { [id: string]: number },
      );
      const updated = reorderPriorities(priorities, order);
      const changed = Object.keys(priorities).filter(
        (id) => priorities[id] !== updated[id],
      );
      const updatedWorkingLists = changed
        .map((id) => {
          const list = lists.find((wl) => wl.ID === id);
          if (!list) return null;

          return {
            ...list,
            Priority: updated[id],
          };
        })
        .filter((wl): wl is ace_index.WorkingList => !!wl);
      const mutations = updatedWorkingLists.map((wl) => update.mutateAsync(wl));
      await Promise.all(mutations);
      setQueue([]);
    },
    onDragStart(workingList: ace_index.WorkingList, i: number) {
      return (e: React.DragEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();

        setDrag({
          workingList: workingList,
          index: i,
          rect,
        });

        e.dataTransfer.effectAllowed = 'move';
      };
    },
    onDragOver(workingList: ace_index.WorkingList, i: number) {
      return (e: React.DragEvent<HTMLDivElement>) => {
        if (!drag) return;
        e.preventDefault();
        if (drag.workingList.ID === workingList.ID) return;
        e.dataTransfer.dropEffect = 'move';
        const index = i >= drag.index ? i + 1 : i;
        setDrag((drag) => (drag ? { ...drag, index } : undefined));
      };
    },
    onDragEnd(e: React.DragEvent<HTMLDivElement>) {
      if (!drag) return;
      e.preventDefault();
      setQueue((queue) => [...queue, drag]);
      setDrag(undefined);
    },
  };
}

export function ReorderWorkingLists({ reorder }: { reorder: Reorder }) {
  return (
    <FilterSet>
      {reorder.ordered.map((workingList, i) => {
        return (
          <div
            draggable={!reorder.isLoading}
            key={workingList.ID}
            style={{ cursor: 'move' }}
            onDragStart={reorder.onDragStart(workingList, i)}
            onDragOver={reorder.onDragOver(workingList, i)}
            onDragEnd={reorder.onDragEnd}
          >
            <WorkingListCard
              highlighted={reorder.dragging?.ID === workingList.ID}
              disabled={reorder.isLoading}
            >
              <CardRow>
                <Icon icon="move" intent="primary" />
                <span>{workingList.Name}</span>
              </CardRow>
            </WorkingListCard>
          </div>
        );
      })}
    </FilterSet>
  );
}
